/**
 * StageSyncBar — assumed vs digest-inferred stages with optional auto-sync.
 *
 * Lives at the top of the Stage tab (relocated Feb 2026 from the Claims tab).
 *
 * Display modes:
 *   • digest_authoritative = false  →  3 tiles + a one-line gap summary
 *       ASSUMED / YOUR INFERRED / THEIR INFERRED
 *   • digest_authoritative = true   →  2 tiles (no gap line — values synched)
 *       YOUR STAGE (synched) / THEIR INFERRED
 *
 * Evidence text is rendered as small italic copy BENEATH each tile (no
 * hover-gated tooltips). The digest's asynchronicity_flag still surfaces as
 * an amber alert below the tiles.
 */
import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Loader2, MessageSquarePlus } from "lucide-react";
import { authAxios } from "../../utils/auth";
import { Switch } from "../ui/switch";

const TILE_TONE = {
  assumed: "border-divine-gold/40 bg-divine-gold/10 text-divine-gold",
  user: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  match: "border-sky-500/40 bg-sky-500/10 text-sky-300",
  synced: "border-violet-500/40 bg-violet-500/10 text-violet-300",
};

function formatStage(value, label) {
  if (value === null || value === undefined) return "—";
  return label ? `${value}. ${label}` : String(value);
}

function StageTile({ tone, header, value, label, testid }) {
  const cls = TILE_TONE[tone] || TILE_TONE.assumed;
  return (
    <div className="flex-1 min-w-0 flex flex-col" data-testid={testid}>
      <div className={`rounded-md border px-3 py-2 ${cls}`}>
        <div className="text-[10px] uppercase tracking-wider opacity-80">
          {header}
        </div>
        <div className="text-base font-semibold mt-0.5 leading-tight break-words">
          {formatStage(value, label)}
        </div>
      </div>
    </div>
  );
}

export default function StageSyncBar({ matchId, refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!matchId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await authAxios.get(`/relationship/${matchId}/stage-sync`, {
        params: { _: Date.now() },
        headers: { "Cache-Control": "no-store" },
      });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load stage sync");
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const handleToggle = async (next) => {
    if (!matchId) return;
    setToggling(true);
    try {
      await authAxios.patch(`/relationship/${matchId}/stage-sync`, {
        digest_authoritative: next,
      });
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Toggle failed");
    } finally {
      setToggling(false);
    }
  };

  if (!matchId) return null;

  if (loading && !data) {
    return (
      <div
        data-testid="stage-sync-loading"
        className="flex items-center gap-2 text-white/50 text-xs p-3 border border-white/10 rounded-md"
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Loading stage sync…
      </div>
    );
  }

  if (error) {
    return (
      <div
        data-testid="stage-sync-error"
        className="text-rose-300 text-xs p-3 border border-rose-500/30 rounded-md bg-rose-500/5"
      >
        {error}
      </div>
    );
  }

  if (!data) return null;

  const {
    persona_connection,
    persona_connection_label,
    digest_authoritative,
    inferred_user_stage,
    inferred_match_stage,
    asynchronicity_flag,
    asynchronicity_reason,
    user_persona_name,
    match_name,
  } = data;

  const userName = user_persona_name || "You";
  const matchName = match_name || "this person";
  const userVal = inferred_user_stage?.value ?? null;
  const userLabel = inferred_user_stage?.label ?? null;
  const matchVal = inferred_match_stage?.value ?? null;
  const matchLabel = inferred_match_stage?.label ?? null;

  // Build the one-line assumed-vs-digest gap summary + "Explore this" CTA
  // (OFF mode only; only when assumed and inferred-user actually differ).
  let gapSection = null;
  if (!digest_authoritative && persona_connection && userVal && persona_connection !== userVal) {
    const gap = Math.abs(persona_connection - userVal);
    const stages = gap === 1 ? "stage" : "stages";
    const direction = persona_connection > userVal ? "ahead of" : "behind";
    const assumedFmt = formatStage(persona_connection, persona_connection_label);
    const inferredFmt = formatStage(userVal, userLabel);

    // Templated calibration question (free, predictable). User can edit
    // before sending — chip just lowers activation energy.
    const exploreText =
      `I want to dig into something. I've been seeing my connection with ` +
      `${matchName} as ${assumedFmt}, but you've been reading me as ` +
      `${inferredFmt} across our recent sessions — a ${gap}-${stages === "stage" ? "stage" : "stage"} gap. ` +
      `Can you walk me through what you're seeing that I might be missing, ` +
      `and where you think the truth actually sits?`;

    const handleExplore = () => {
      window.dispatchEvent(
        new CustomEvent("eros:inject-prompt", { detail: { text: exploreText } })
      );
    };

    gapSection = (
      <div className="space-y-2">
        <p
          data-testid="stage-sync-gap-summary"
          className="text-[11.5px] text-white/70 leading-snug border-l-2 border-divine-gold/40 pl-2.5"
        >
          You set the relationship at{" "}
          <strong className="text-divine-gold">{assumedFmt}</strong>; Eros's
          read of you across recent sessions is{" "}
          <strong className="text-emerald-300">{inferredFmt}</strong> — your
          assumption sits {gap} {stages} {direction} the coaching reading,
          drawn from the trajectory below.
        </p>
        <button
          type="button"
          onClick={handleExplore}
          data-testid="stage-sync-explore-btn"
          className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded border border-divine-gold/40 bg-divine-gold/10 text-divine-gold hover:bg-divine-gold/20 hover:border-divine-gold/60 transition-colors focus:outline-none focus:ring-2 focus:ring-divine-gold/40"
          title="Pre-fills your chat input with a question about this gap, ready to send or edit"
        >
          <MessageSquarePlus className="w-3.5 h-3.5" />
          Explore this in chat
        </button>
      </div>
    );
  }

  return (
    <section
      data-testid="stage-sync-bar"
      className="space-y-2 border border-divine-gold/15 rounded-md p-3 bg-divine-black/30"
    >
      <header className="flex items-center justify-between gap-2">
        <h4 className="text-divine-gold/80 text-xs uppercase tracking-wide">
          Connection stage
        </h4>
        <label
          className="flex items-center gap-2 text-[11px] text-white/70 cursor-pointer select-none"
          title="When ON, the coaching read of where you sit auto-syncs into your match profile after each session. When OFF, you stay the authority and the panel highlights any disconnect."
        >
          <span className="whitespace-nowrap">Sync from Coaching</span>
          <Switch
            data-testid="stage-sync-toggle"
            checked={!!digest_authoritative}
            disabled={toggling}
            onCheckedChange={handleToggle}
            className="data-[state=checked]:bg-divine-gold data-[state=unchecked]:bg-white/15 border-divine-gold/30"
          />
        </label>
      </header>

      <div className="flex flex-col sm:flex-row gap-3">
        {digest_authoritative ? (
          <>
            <StageTile
              tone="synced"
              header={`${userName} (synched)`}
              value={userVal ?? persona_connection}
              label={userLabel ?? persona_connection_label}
              testid="stage-tile-synced"
            />
            <StageTile
              tone="match"
              header={`${matchName} (inferred)`}
              value={matchVal}
              label={matchLabel}
              testid="stage-tile-match"
            />
          </>
        ) : (
          <>
            <StageTile
              tone="assumed"
              header={`${userName} (assumed)`}
              value={persona_connection}
              label={persona_connection_label}
              testid="stage-tile-assumed"
            />
            <StageTile
              tone="user"
              header={`${userName} (inferred)`}
              value={userVal}
              label={userLabel}
              testid="stage-tile-user"
            />
            <StageTile
              tone="match"
              header={`${matchName} (inferred)`}
              value={matchVal}
              label={matchLabel}
              testid="stage-tile-match"
            />
          </>
        )}
      </div>

      {gapSection}

      {asynchronicity_flag ? (
        <div
          data-testid="stage-sync-async-alert"
          className="flex items-start gap-2 text-amber-300 text-xs leading-snug border border-amber-500/30 bg-amber-500/5 rounded-md px-2.5 py-2"
        >
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">Asynchronicity detected</div>
            {asynchronicity_reason ? (
              <div className="text-amber-200/80 mt-0.5">
                {asynchronicity_reason}
              </div>
            ) : (
              <div className="text-amber-200/80 mt-0.5">
                The coaching read sees a meaningful gap between where you sit and
                where they sit. Worth raising next session.
              </div>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}
